import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await register(email, password, name);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-8" data-testid="register-success">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-sm p-10 text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto text-[#16A34A]" strokeWidth={1.5} />
          <h2 className="mt-6 font-heading text-2xl font-bold text-zinc-900">Request submitted</h2>
          <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
            Your account is pending admin approval. You'll be able to sign in
            once your access is granted.
          </p>
          <Link to="/login" data-testid="register-back-to-login">
            <Button variant="outline" className="mt-6 rounded-sm border-zinc-200">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to sign in
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-8" data-testid="register-page">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-10 space-y-6" data-testid="register-form">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            Marketing Image Generator
          </div>
          <h2 className="mt-2 font-heading text-3xl font-bold text-zinc-900">Request access</h2>
          <p className="mt-2 text-sm text-zinc-500">An admin will review and approve your account.</p>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-sm font-medium text-zinc-700">Name</Label>
            <Input
              id="name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe" className="mt-1 rounded-sm border-zinc-200"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-zinc-700">Email</Label>
            <Input
              id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              placeholder="you@company.com" className="mt-1 rounded-sm border-zinc-200"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-sm font-medium text-zinc-700">Password</Label>
            <Input
              id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              minLength={6} placeholder="At least 6 characters" className="mt-1 rounded-sm border-zinc-200"
              data-testid="register-password-input"
            />
          </div>
        </div>

        <Button
          type="submit" disabled={busy}
          className="w-full rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-11"
          data-testid="register-submit-button"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
        </Button>

        <div className="text-center text-sm text-zinc-500">
          Already have access?{" "}
          <Link to="/login" className="text-[#0055FF] font-medium hover:underline" data-testid="link-to-login">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
